/**
 * UserManagementPage Component
 * 사용자 관리 페이지
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useUserData } from '../hooks/useUserData';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { usePagination } from '@/shared/hooks/usePagination';
import Loading from '@/shared/components/feedback/Loading';
import UserDetailModal from './UserDetailModal';
import FilterSection from './FilterSection';
import UserTable from './UserTable';
import { User, UpdateUserRequestDto, CreateUserRequestDto } from '../types/user.types';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { USER_ITEMS_PER_PAGE, SYSTEM_STORAGE_KEYS } from '../../shared/config/system.config';
import {
    FilterState,
    INITIAL_FILTERS,
    buildSearchParams,
    USER_TYPE_OPTIONS,
} from './userManagement.helpers';
import '@/shared/styles/common-table.css';
import '../styles/UserManagementPage.css';

// ======== 컴포넌트 ========

const UserManagementPage: React.FC = () => {
    // ======== 훅 ========

    const { users, loading, prmCodes, fetchUsers, createUser, updateUser, initializeUsers } = useUserData();
    const showLoading = useDelayedLoading(loading);

    // C타입(본사) 사용자는 관리자(A) 옵션 제외
    const currentPrmCode = getItem<string>('PRM_CODE');
    const availableUserTypeOptions = currentPrmCode === 'C'
        ? USER_TYPE_OPTIONS.filter(opt => opt.value !== 'A')
        : [...USER_TYPE_OPTIONS];

    // ======== 상태 ========

    const [filters, setFilters] = useState<FilterState>(() => {
        return loadFilters<FilterState>(SYSTEM_STORAGE_KEYS.USER_MANAGEMENT_FILTERS) || INITIAL_FILTERS;
    });
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 키워드(이름/ID) client-side 필터링
    const filteredUsers = filters.keyword
        ? users.filter(u =>
            u.userId.toLowerCase().includes(filters.keyword.toLowerCase()) ||
            u.userName.toLowerCase().includes(filters.keyword.toLowerCase())
        )
        : users;

    const { currentPage, totalPages, currentItems, goToPage, resetPagination } = usePagination({
        items: filteredUsers,
        itemsPerPage: USER_ITEMS_PER_PAGE,
    });

    // ======== 초기 로드 ========

    /**
     * 페이지 마운트 시 저장된 필터로 사용자 목록 조회
     */
    useEffect(() => {
        const saved = loadFilters<FilterState>(SYSTEM_STORAGE_KEYS.USER_MANAGEMENT_FILTERS);
        initializeUsers(saved ? buildSearchParams(saved) : undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ======== 검색 핸들러 ========

    /**
     * 검색 실행 (필터 저장 포함)
     */
    const handleSearch = useCallback(() => {
        resetPagination();
        fetchUsers(buildSearchParams(filters));
        saveFilters(SYSTEM_STORAGE_KEYS.USER_MANAGEMENT_FILTERS, filters);
    }, [resetPagination, fetchUsers, filters]);

    // ======== 모달 핸들러 ========

    /**
     * 사용자 행 클릭 (수정 모달 열기)
     */
    const handleUserClick = useCallback((user: User) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    }, []);

    /**
     * 신규 사용자 등록 버튼 클릭
     */
    const handleNewUser = useCallback(() => {
        setSelectedUser(null);
        setIsModalOpen(true);
    }, []);

    /**
     * 모달 닫기
     */
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedUser(null);
    }, []);

    // ======== CRUD 핸들러 ========

    /**
     * 사용자 수정 완료
     */
    const handleUpdateUser = useCallback(async (userId: string, request: UpdateUserRequestDto) => {
        const result = await updateUser(userId, request);
        if (result) handleCloseModal();
    }, [updateUser, handleCloseModal]);

    /**
     * 사용자 생성 완료
     */
    const handleCreateUser = useCallback(async (request: CreateUserRequestDto) => {
        const result = await createUser(request);
        if (result) handleCloseModal();
    }, [createUser, handleCloseModal]);

    // ======== 조기 반환 ========

    if (showLoading) {
        return <Loading />;
    }

    // ======== 렌더링 ========

    return (
        <div className="user-management-page">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">사용자 관리</h1>
            </div>

            {/* 필터 섹션 */}
            <FilterSection
                filters={filters}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onNewUser={handleNewUser}
                userTypeOptions={availableUserTypeOptions}
            />

            {/* 사용자 목록 테이블 */}
            <UserTable
                users={filteredUsers}
                currentItems={currentItems}
                currentPage={currentPage}
                totalPages={totalPages}
                onUserClick={handleUserClick}
                onPageChange={goToPage}
            />

            {/* 사용자 상세/등록 모달 */}
            {isModalOpen && (
                <UserDetailModal
                    user={selectedUser}
                    prmCodes={prmCodes}
                    mode={selectedUser ? 'edit' : 'create'}
                    onClose={handleCloseModal}
                    onUpdate={handleUpdateUser}
                    onCreate={handleCreateUser}
                />
            )}
        </div>
    );
};

export default UserManagementPage;
